import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import AddNewParkingForm from 'src/features/parkingAdmin/components/AddNewParkingForm';
import {
    createParkingFormSchema,
    type CreateParkingFormSchemaValues,
    useCreateParking,
} from 'src/features/parkingAdmin/hooks/useCreateParking';
import parkingAdminMessages from 'src/features/parkingAdmin/utils/messages';
import { PARKINGS_RESOURCE } from 'src/features/parkingManagement/api/getParkings';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { Parking } from 'src/features/parkingManagement/types/parking';

interface AddNewParkingModalProps extends DataTableNewEntryProps<Parking> {}

const AddNewParkingModal = ({ className, table }: AddNewParkingModalProps) => {
    const { formatMessage } = useIntl();
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

    const nextParkingNumber = React.useMemo(() => {
        return (table?.options.data?.length ?? 0) + 1;
    }, [table?.options.data?.length]);

    const defaultValues = {
        name: undefined,
        system_id: undefined,
        serial_id: nextParkingNumber,
        country: undefined,
        city: undefined,
        address: undefined,
        default_currency: undefined,
        number_of_floors: undefined,
        number_of_spaces: undefined,
        is_public: false,
        is_featured: false,
        enable_online_payments: false,
        enable_online_reservations: false,
        longitude: undefined,
        latitude: undefined,
    };

    const form = useForm<CreateParkingFormSchemaValues>({
        resolver: zodResolver(createParkingFormSchema),
        defaultValues: {},
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [nextParkingNumber]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset(defaultValues);
        }
        setIsModalOpen(isOpen);
    };

    const { mutate, isPending } = useCreateParking({
        onSuccess: () => {
            onOpenChange(false);
            void queryClient.invalidateQueries({ queryKey: [PARKINGS_RESOURCE] });
            toast({
                title: formatMessage(parkingAdminMessages.createdSuccessfully),
                description: formatMessage(parkingAdminMessages.createParkingSuccessfulDescription),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit: SubmitHandler<CreateParkingFormSchemaValues> = (values: CreateParkingFormSchemaValues) => {
        const { longitude, latitude } = values;

        const dirtyFields = form.formState.dirtyFields;

        const editedFields = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof CreateParkingFormSchemaValues;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Partial<Record<keyof CreateParkingFormSchemaValues, any>>,
        );

        const hasCoordinates =
            longitude !== undefined &&
            latitude !== undefined &&
            longitude !== '' &&
            latitude !== '' &&
            !isNaN(Number(longitude)) &&
            !isNaN(Number(latitude));

        const submitValues = {
            serial_id: nextParkingNumber,
            ...editedFields,
            ...(hasCoordinates && {
                coordinates: {
                    type: 'Point',
                    coordinates: [parseFloat(longitude), parseFloat(latitude)],
                },
            }),
        };

        mutate(submitValues);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogTrigger asChild>
                <Button variant="outline" className={cn('ml-auto', className)}>
                    <FontAwesomeIcon className="text-primary-700" icon={faPlus} />
                    {formatMessage(tableMessages.new, {
                        entry: formatMessage(parkingAdminMessages.parking).toLowerCase(),
                    })}
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faPlus} />
                        <div>
                            <DialogTitle>
                                {formatMessage(tableMessages.new, {
                                    entry: formatMessage(parkingAdminMessages.parking).toLowerCase(),
                                })}
                            </DialogTitle>
                            <DialogDescription>{formatMessage(parkingAdminMessages.addNewParking)}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <AddNewParkingForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(parkingAdminMessages.addNewParking)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AddNewParkingModal;
